"use client";

import { Slider as SliderPrimitive } from "@base-ui/react/slider";
import * as React from "react";

import { cn } from "@/lib/utils";

type SliderRootProps = Omit<
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>,
  "onValueChange" | "onValueCommitted"
> & {
  /** Applied to the filled indicator (Base UI `Indicator`; Radix `Range`). */
  rangeClassName?: string;
  onValueChange?: (value: number[]) => void;
  /** Radix-compatible alias for Base UI `onValueCommitted`. */
  onValueCommit?: (value: number[]) => void;
};

function normalizeSliderValue(value: number | readonly number[]): number[] {
  if (typeof value === "number") {
    return [value];
  }
  return [...value];
}

const SliderRoot = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderRootProps
>(
  (
    {
      className,
      children,
      rangeClassName,
      onValueChange,
      onValueCommit,
      ...props
    },
    ref
  ) => (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        "relative flex w-full touch-none items-center select-none",
        className
      )}
      onValueChange={(value) => {
        onValueChange?.(normalizeSliderValue(value));
      }}
      onValueCommitted={(value) => {
        onValueCommit?.(normalizeSliderValue(value));
      }}
      {...props}
    >
      <SliderPrimitive.Control className="flex w-full grow items-center">
        {/* No overflow-hidden: the Thumb renders inside the Track, and clipping
            it to the rail's height made it invisible and undraggable. */}
        <SliderPrimitive.Track className="bg-grey-100 relative h-1.5 w-full grow rounded-full">
          <SliderPrimitive.Indicator
            className={cn("absolute h-full rounded-full", rangeClassName)}
          />
          {children}
        </SliderPrimitive.Track>
      </SliderPrimitive.Control>
    </SliderPrimitive.Root>
  )
);
SliderRoot.displayName = "SliderRoot";

const SliderThumb = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Thumb>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Thumb>
>(({ className, index = 0, ...props }, ref) => (
  <SliderPrimitive.Thumb
    ref={ref}
    index={index}
    className={cn(
      "bg-pulse-700 focus-visible:ring-pulse-500 block size-4 cursor-pointer rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    {...props}
  />
));
SliderThumb.displayName = "SliderThumb";

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderRoot>,
  React.ComponentPropsWithoutRef<typeof SliderRoot>
>(({ className, ...props }, ref) => (
  <SliderRoot ref={ref} {...props} className={className}>
    <SliderThumb />
  </SliderRoot>
));
Slider.displayName = "Slider";

export { Slider, SliderRoot, SliderThumb };
